import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSoftphone } from './SoftphoneContext';
import { isNativeApp } from '../../utils/native';

const KEYS = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '*', '0', '#'];
// In the mobile app the number is typed on our keypad only: the phone's own
// keyboard would cover the screen.
const NATIVE = isNativeApp();

/**
 * Number field + keypad + Ara / ⌫. Used by the corner dialer and the phone
 * page. `onPlaced()` runs after a call was started.
 */
export default function Dialer({ onPlaced, large = false }) {
  const { t } = useTranslation();
  const sp = useSoftphone();
  const [number, setNumber] = useState('');
  const [dialError, setDialError] = useState('');
  if (!sp) return null;

  const placeCall = () => {
    setDialError('');
    try {
      sp.dial(number);
      setNumber('');
      if (onPlaced) onPlaced();
    } catch (e) {
      setDialError(e.message || t('shell.softphone.callStartFailed'));
    }
  };
  const key = large ? 'py-3 text-xl rounded-full' : 'py-1.5 text-sm rounded';

  return (
    <form onSubmit={(e) => { e.preventDefault(); placeCall(); }}>
      <input
        autoFocus={!NATIVE && !large}
        inputMode={NATIVE ? 'none' : undefined}
        value={number}
        onChange={(e) => setNumber(e.target.value)}
        placeholder={t('shell.softphone.numberOrExtension')}
        aria-label={t('shell.softphone.numberToCall')}
        className={`w-full px-3 py-2 border rounded-lg tracking-wide dark:bg-gray-900 dark:border-gray-600 ${large ? 'text-2xl text-center' : 'text-lg'}`}
      />
      <div className={`mt-2 grid grid-cols-3 ${large ? 'gap-3' : 'gap-1'}`}>
        {KEYS.map((k) => (
          <button key={k} type="button" onClick={() => setNumber((n) => n + k)}
            className={`border hover:bg-gray-50 dark:hover:bg-gray-700 ${key}`}>
            {k}
          </button>
        ))}
      </div>
      {dialError && <p className="mt-2 text-xs text-red-600">{dialError}</p>}
      {(sp.error && !dialError) && <p className="mt-2 text-xs text-red-600">{sp.error}</p>}
      <div className={`mt-2 flex ${large ? 'gap-3' : 'gap-1'}`}>
        <button type="submit" disabled={sp.status !== 'registered' || !number.trim()}
          className={`flex-1 rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50 ${large ? 'py-3 text-base' : 'py-2 text-sm'}`}>
          {t('shell.softphone.call')}
        </button>
        <button type="button" onClick={() => setNumber((n) => n.slice(0, -1))} disabled={!number}
          aria-label={t('common.delete')} title={t('common.delete')}
          className="w-14 rounded-lg border text-lg hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-40">
          ⌫
        </button>
      </div>
    </form>
  );
}
